import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./RetouchPaywall.module.scss"

// ---------------------------------------------------------------------------
// "Retouch" — the pitch is NOT privacy / storage / backup. It's EDITING —
// making every photo its best version in one tap. None of the prior 19
// paywalls pitched enhancement; they all stored, found, shared or preserved
// photos. This one improves them.
//
// The screen is a WORKING PHOTO EDITOR, not a hero + card. A light studio:
// a live canvas that starts dull, crooked and hazy, a rail of real editing
// tools you tap, and an "Adjustments" ledger that builds up as each edit
// stacks onto the same frame. An "Auto-Retouch" scan runs the whole recipe
// on load to teach the gesture; hold "Compare" to snap back to the original.
// A filmstrip of the rest of your roll invites you to retouch them all.
// Photographic, additive-edit interaction, playful-confident register.
// Assets are pre-fetched into public/retouch so it renders on debug data.
// ---------------------------------------------------------------------------

const PUB = process.env.PUBLIC_URL ?? ""
const src = (file: string) => `${PUB}/retouch/${file}.jpg`

type ToolKey = "light" | "color" | "straighten" | "sharpen" | "portrait"

type Tool = {
  key: ToolKey
  label: string
  delta: string // shown in the ledger once applied
  hint: string
  icon: JSX.Element
}

const TOOLS: Tool[] = [
  {
    key: "light",
    label: "Light",
    delta: "+38",
    hint: "Exposure & contrast",
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
        <circle
          cx="12"
          cy="12"
          r="4.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
        />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
          <line
            key={a}
            x1="12"
            y1="3"
            x2="12"
            y2="5.4"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            transform={`rotate(${a} 12 12)`}
          />
        ))}
      </svg>
    ),
  },
  {
    key: "color",
    label: "Colour",
    delta: "+52",
    hint: "Vibrance & white balance",
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
        <path
          d="M12 3s6 6.4 6 10.4A6 6 0 1 1 6 13.4C6 9.4 12 3 12 3Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    key: "straighten",
    label: "Straighten",
    delta: "−1.8°",
    hint: "Level the horizon",
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
        <path
          d="M4 17h16M4 17l3-3M4 17l3 3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 13.5 18.5 6"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    key: "sharpen",
    label: "Sharpen",
    delta: "+30",
    hint: "Detail & clarity",
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
        <path
          d="M12 3 5 12l7 9 7-9z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinejoin="round"
        />
        <path
          d="M12 3v18M5 12h14"
          stroke="currentColor"
          strokeWidth="1.4"
          opacity=".55"
        />
      </svg>
    ),
  },
  {
    key: "portrait",
    label: "Portrait",
    delta: "On",
    hint: "Soften skin, lift the eyes",
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
        <circle
          cx="12"
          cy="9"
          r="3.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
        />
        <path
          d="M5.5 19.5c.9-3.6 3.4-5.4 6.5-5.4s5.6 1.8 6.5 5.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
]

const ORDER: ToolKey[] = ["straighten", "light", "color", "sharpen", "portrait"]

type Frame = {
  img: string
  title: string
  meta: string
}

const ROLL: Frame[] = [
  { img: "portrait", title: "Golden hour", meta: "IMG_4471 · f/1.8" },
  { img: "lake", title: "The overlook", meta: "IMG_3902 · 6:41pm" },
  { img: "dog", title: "Full sprint", meta: "IMG_5120 · burst" },
  { img: "cake", title: "She’s five", meta: "IMG_4788 · 1/60s" },
  { img: "kids", title: "Last one home", meta: "IMG_5551 · dusk" },
  { img: "couple", title: "The picnic", meta: "IMG_4610 · f/2.2" },
  { img: "food", title: "Sunday brunch", meta: "IMG_5033 · macro" },
  { img: "surf", title: "Last set", meta: "IMG_4955 · 1/1000s" },
]

// Compute the live CSS filter + transform from the set of applied tools.
// Everything starts "raw" (dull, warm-cast, soft, crooked) and each tool
// pushes a specific channel toward the graded look.
function grade(applied: Set<ToolKey>, raw: boolean) {
  if (raw) applied = new Set()
  const has = (k: ToolKey) => applied.has(k)
  const brightness = 0.8 + (has("light") ? 0.26 : 0) + (has("portrait") ? 0.05 : 0)
  const contrast = 0.8 + (has("light") ? 0.1 : 0) + (has("sharpen") ? 0.18 : 0)
  const saturate = 0.5 + (has("color") ? 0.74 : 0) + (has("portrait") ? 0.06 : 0)
  const sepia = 0.18 - (has("color") ? 0.18 : 0)
  const blur = 0.9 - (has("sharpen") ? 0.9 : 0)
  const rotate = has("straighten") ? 0 : -1.9
  const scale = has("straighten") ? 1.0 : 1.045
  return {
    filter: `brightness(${brightness}) contrast(${contrast}) saturate(${saturate}) sepia(${sepia}) blur(${blur}px)`,
    transform: `rotate(${rotate}deg) scale(${scale})`,
  }
}

function RetouchPaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct ? formatCurrency(yearlyProduct.price, locale) : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""
  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round((1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  // ── editor state ──────────────────────────────────────────────────────────
  const [active, setActive] = useState(0)
  const [applied, setApplied] = useState<Set<ToolKey>>(new Set())
  const [comparing, setComparing] = useState(false)
  const [auto, setAuto] = useState(false)
  const [done, setDone] = useState<Set<number>>(new Set())
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  const timers = useRef<number[]>([])
  const frame = ROLL[active]
  const count = applied.size
  const complete = count === TOOLS.length
  const pct = Math.round((count / TOOLS.length) * 100)

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }

  // Run the whole recipe in sequence, with a scanning sweep across the frame.
  const runAuto = useCallback(() => {
    clearTimers()
    setApplied(new Set())
    setComparing(false)
    setAuto(true)
    ORDER.forEach((k, i) => {
      const t = window.setTimeout(
        () => {
          setApplied((prev) => {
            const next = new Set(prev)
            next.add(k)
            return next
          })
        },
        360 + i * 520,
      )
      timers.current.push(t)
    })
    const end = window.setTimeout(() => setAuto(false), 360 + ORDER.length * 520 + 500)
    timers.current.push(end)
  }, [])

  // Teach the gesture once on load.
  useEffect(() => {
    if (reduce) {
      setApplied(new Set(ORDER))
      return
    }
    const t = window.setTimeout(runAuto, 650)
    timers.current.push(t)
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Mark a frame retouched once its recipe is complete.
  useEffect(() => {
    if (complete) {
      setDone((prev) => {
        if (prev.has(active)) return prev
        const next = new Set(prev)
        next.add(active)
        return next
      })
    }
  }, [complete, active])

  const toggle = (k: ToolKey) => {
    setAuto(false)
    clearTimers()
    setApplied((prev) => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })
  }

  const loadFrame = (i: number) => {
    if (i === active) return
    setAuto(false)
    clearTimers()
    setActive(i)
    setApplied(new Set())
    setComparing(false)
  }

  const applyAll = () => {
    setAuto(false)
    clearTimers()
    setApplied(new Set(ORDER))
  }

  const g = grade(applied, comparing)
  const showRaw = comparing || (count === 0 && !auto)

  const handleClose = () => native.postNativeEvent("close")
  const buy = () => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (p) native.subscribe(p)
  }

  // circumference for the score ring
  const R = 15.5
  const C = 2 * Math.PI * R

  return (
    <div className={styles.page}>
      {/* editor chrome ------------------------------------------------------- */}
      <header className={styles.top}>
        <button className={styles.close} onClick={handleClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <span className={styles.brand}>
          CloudVault <b>Studio</b>
        </span>
        <span className={styles.frameNo}>{frame.meta.split(" · ")[0]}</span>
      </header>

      {/* the canvas --------------------------------------------------------- */}
      <div className={styles.stageWrap}>
        <div className={`${styles.stage} ${auto ? styles.scanning : ""}`}>
          <img
            key={frame.img}
            className={styles.photo}
            src={src(frame.img)}
            alt={frame.title}
            style={g}
            draggable={false}
          />
          {/* portrait bloom */}
          <div
            className={styles.bloom}
            style={{ opacity: applied.has("portrait") && !comparing ? 1 : 0 }}
          />
          {/* grain that only the raw frame carries */}
          <div
            className={styles.grain}
            style={{ opacity: showRaw ? 0.5 : applied.has("sharpen") ? 0 : 0.22 }}
          />
          {/* scanning light bar during auto-retouch */}
          {auto && !reduce && <div className={styles.scan} />}

          {/* state stamp */}
          <div className={`${styles.stamp} ${showRaw ? styles.stampRaw : ""}`}>
            {showRaw ? "ORIGINAL" : complete ? "RETOUCHED ✓" : "RETOUCHING…"}
          </div>

          {/* score ring */}
          <div className={styles.ring}>
            <svg viewBox="0 0 40 40" width="42" height="42">
              <circle cx="20" cy="20" r={R} className={styles.ringBg} />
              <circle
                cx="20"
                cy="20"
                r={R}
                className={styles.ringFg}
                strokeDasharray={C}
                strokeDashoffset={C * (1 - count / TOOLS.length)}
                transform="rotate(-90 20 20)"
              />
            </svg>
            <span className={styles.ringNum}>{pct}</span>
          </div>

          {/* caption */}
          <div className={styles.caption}>
            <span className={styles.capTitle}>{frame.title}</span>
            <span className={styles.capMeta}>{frame.meta}</span>
          </div>

          {/* compare — press & hold to see the untouched original */}
          <button
            className={styles.compare}
            onPointerDown={() => setComparing(true)}
            onPointerUp={() => setComparing(false)}
            onPointerLeave={() => setComparing(false)}
            onPointerCancel={() => setComparing(false)}
          >
            Hold to compare
          </button>
        </div>

        {/* the adjustments ledger — builds up as edits stack */}
        <div className={styles.ledger}>
          {count === 0 ? (
            <span className={styles.ledgerEmpty}>No adjustments yet</span>
          ) : (
            TOOLS.filter((t) => applied.has(t.key)).map((t) => (
              <span key={t.key} className={styles.ledgerItem}>
                {t.label}
                <b>{t.delta}</b>
              </span>
            ))
          )}
        </div>
      </div>

      {/* tool rail ----------------------------------------------------------- */}
      <div className={styles.rail}>
        {TOOLS.map((t) => {
          const on = applied.has(t.key)
          return (
            <button
              key={t.key}
              className={`${styles.tool} ${on ? styles.toolOn : ""}`}
              onClick={() => toggle(t.key)}
            >
              <span className={styles.toolIcon}>
                {on ? (
                  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
                    <path
                      d="M5 12.5l4.2 4.2L19 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  t.icon
                )}
              </span>
              <span className={styles.toolLabel}>{t.label}</span>
              <span className={styles.toolHint}>{on ? t.delta : t.hint}</span>
            </button>
          )
        })}
      </div>

      <div className={styles.actions}>
        <button className={styles.auto} onClick={runAuto}>
          <span className={styles.spark}>✦</span> Auto-Retouch
        </button>
        <button
          className={styles.applyAll}
          onClick={applyAll}
          disabled={complete}
        >
          Apply all
        </button>
      </div>

      {/* the pitch ----------------------------------------------------------- */}
      <div className={styles.pitch}>
        <h1>
          Every photo, <em>its best version.</em>
        </h1>
        <p>
          One tap corrects the light, the colour, the crooked horizon and the
          soft focus — on the original, never overwriting it. Premium turns the
          whole Studio loose on your entire library.
        </p>
      </div>

      {/* the rest of the roll ------------------------------------------------ */}
      <div className={styles.rollWrap}>
        <div className={styles.rollHead}>
          <span>Your library</span>
          <span className={styles.rollCount}>
            <b>{done.size}</b> retouched · 12,480 waiting
          </span>
        </div>
        <div className={styles.roll}>
          {ROLL.map((f, i) => (
            <button
              key={f.img}
              className={`${styles.chip} ${i === active ? styles.chipOn : ""}`}
              onClick={() => loadFrame(i)}
            >
              <img src={src(f.img)} alt={f.title} draggable={false} />
              {done.has(i) && (
                <span className={styles.chipDone}>
                  <svg viewBox="0 0 24 24" width="12" height="12">
                    <path
                      d="M5 12.5l4.2 4.2L19 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* the export bar — the offer ----------------------------------------- */}
      <div className={styles.offer}>
        <div className={styles.plans}>
          <button
            className={`${styles.plan} ${plan === "yearly" ? styles.planOn : ""}`}
            onClick={() => setPlan("yearly")}
          >
            <div className={styles.planTop}>
              <span className={styles.planName}>Yearly</span>
              {savings > 0 && <span className={styles.save}>SAVE {savings}%</span>}
            </div>
            <div className={styles.planPrice}>
              {yearlyPrice}
              <span className={styles.per}>/yr</span>
            </div>
            <div className={styles.planNote}>
              just {yearlyPerMonth}/mo, billed yearly
            </div>
          </button>

          <button
            className={`${styles.plan} ${styles.planMini} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
          >
            <div className={styles.planName}>Monthly</div>
            <div className={styles.planPriceMini}>
              {monthlyPrice}
              <span className={styles.per}>/mo</span>
            </div>
            <div className={styles.planNote}>flexible</div>
          </button>
        </div>

        <button className={styles.cta} onClick={buy}>
          <span className={styles.spark}>✦</span>
          {plan === "yearly"
            ? `Unlock Studio — ${yearlyPrice}/yr`
            : `Unlock Studio — ${monthlyPrice}/mo`}
        </button>

        <p className={styles.reassure}>
          Edits are non-destructive — your originals are always kept, full
          resolution. Cancel anytime.
        </p>
        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>Privacy</button>
        </div>
      </div>
    </div>
  )
}

export default RetouchPaywall
